import React, { useMemo, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { api } from '../../../lib/api';
import { useToast } from '../../../components/ui/Toast';
import { Button, Card } from '../../../components/ui/Kit';
import Modal from '../../../components/ui/Modal';
import SafeIcon from '../../../common/SafeIcon';
import {
  calculateTotalAnnualIncome,
  calculateTotalAnnualExpenses,
  calculateNetIncome,
} from '../../../lib/financialAnalysisUtils';

const INCOME_CATEGORIES = [
  { value: 'primary', label: 'Primary' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'business', label: 'Business' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'investment', label: 'Investment' },
  { value: 'other', label: 'Other' },
];

const EXPENSE_CATEGORIES = [
  'Housing',
  'Transportation',
  'Living',
  'Healthcare',
  'Family',
  'Debt',
  'Communication',
  'Personal Care',
  'Discretionary',
  'Other',
];

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'weekly', label: 'Weekly' },
];

const cellInput =
  'w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);
}

function incomePayloadFromRow(row) {
  return {
    category: row.category || 'other',
    description: row.description || '',
    amount: Number(row.amount) || 0,
    frequency: row.frequency || 'monthly',
  };
}

function expensePayloadFromRow(row) {
  return {
    category: row.category || 'Other',
    description: row.description || '',
    amount: Number(row.amount) || 0,
    frequency: row.frequency || 'monthly',
  };
}

export default function CashflowTab({
  analysisId,
  agencyId,
  incomeSources,
  expenses,
  setIncomeSources,
  setExpenses,
  scheduleRowSave,
  setSaveStatus,
  markSavedBriefly,
  incomeRef,
  expensesRef,
}) {
  const { addToast } = useToast();
  const [adding, setAdding] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const annualIncome = calculateTotalAnnualIncome(incomeSources);
  const annualExpenses = calculateTotalAnnualExpenses(expenses);
  const netIncome = calculateNetIncome(incomeSources, expenses);

  const expenseGroups = useMemo(() => {
    const order = [...EXPENSE_CATEGORIES];
    const map = new Map();
    for (const row of expenses || []) {
      const cat = row.category || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(row);
    }
    const known = order.filter((c) => map.has(c)).map((c) => ({ category: c, rows: map.get(c) }));
    const extras = [...map.keys()]
      .filter((c) => !order.includes(c))
      .sort()
      .map((c) => ({ category: c, rows: map.get(c) }));
    return [...known, ...extras];
  }, [expenses]);

  // Sequential tab order across Income then Expenses (Amount fields only).
  const amountTabIndexById = useMemo(() => {
    const map = new Map();
    let next = 1;
    for (const row of incomeSources || []) {
      map.set(row.id, next++);
    }
    for (const group of expenseGroups) {
      for (const row of group.rows) {
        map.set(row.id, next++);
      }
    }
    return map;
  }, [incomeSources, expenseGroups]);

  const onIncomeChange = (rowId, field, value) => {
    setIncomeSources((prev) => {
      const next = prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
      incomeRef.current = next;
      return next;
    });
    scheduleRowSave('income', rowId, () => {
      const row = (incomeRef.current || []).find((r) => r.id === rowId);
      return incomePayloadFromRow(row || {});
    });
  };

  const onExpenseChange = (rowId, field, value) => {
    setExpenses((prev) => {
      const next = prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
      expensesRef.current = next;
      return next;
    });
    scheduleRowSave('expenses', rowId, () => {
      const row = (expensesRef.current || []).find((r) => r.id === rowId);
      return expensePayloadFromRow(row || {});
    });
  };

  const addIncome = async () => {
    setAdding('income');
    try {
      const row = await api.financialAnalysis.incomeSources.create(analysisId, {
        agency_id: agencyId,
        category: 'primary',
        description: '',
        amount: 0,
        frequency: 'monthly',
      });
      setIncomeSources((prev) => {
        const next = [...prev, row];
        incomeRef.current = next;
        return next;
      });
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add income source', message: err?.message });
    } finally {
      setAdding(null);
    }
  };

  const addExpense = async () => {
    setAdding('expense');
    try {
      const row = await api.financialAnalysis.expenses.create(analysisId, {
        agency_id: agencyId,
        category: 'Other',
        description: '',
        amount: 0,
        frequency: 'monthly',
      });
      setExpenses((prev) => {
        const next = [...prev, row];
        expensesRef.current = next;
        return next;
      });
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add expense', message: err?.message });
    } finally {
      setAdding(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.kind === 'income') {
        await api.financialAnalysis.incomeSources.delete(deleteTarget.row.id);
        setIncomeSources((prev) => {
          const next = prev.filter((r) => r.id !== deleteTarget.row.id);
          incomeRef.current = next;
          return next;
        });
      } else {
        await api.financialAnalysis.expenses.delete(deleteTarget.row.id);
        setExpenses((prev) => {
          const next = prev.filter((r) => r.id !== deleteTarget.row.id);
          expensesRef.current = next;
          return next;
        });
      }
      setSaveStatus('saved');
      markSavedBriefly();
      setDeleteTarget(null);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete row', message: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Income Sources</h2>
          <Button size="sm" variant="outline" onClick={addIncome} disabled={adding === 'income'} isLoading={adding === 'income'}>
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
            Add Income
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium w-36">Amount</th>
                <th className="px-4 py-3 font-medium w-32">Frequency</th>
                <th className="px-4 py-3 font-medium w-12" />
              </tr>
            </thead>
            <tbody>
              {(incomeSources || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No income sources yet. Add one to get started.
                  </td>
                </tr>
              ) : (
                incomeSources.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50">
                    <td className="px-4 py-2">
                      <select
                        className={cellInput}
                        tabIndex={-1}
                        value={row.category || 'primary'}
                        onChange={(e) => onIncomeChange(row.id, 'category', e.target.value)}
                      >
                        {INCOME_CATEGORIES.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className={cellInput}
                        tabIndex={-1}
                        value={row.description || ''}
                        placeholder="Description"
                        onChange={(e) => onIncomeChange(row.id, 'description', e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        tabIndex={amountTabIndexById.get(row.id)}
                        value={row.amount ?? 0}
                        onChange={(e) => onIncomeChange(row.id, 'amount', e.target.value === '' ? 0 : Number(e.target.value))}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className={cellInput}
                        tabIndex={-1}
                        value={row.frequency || 'monthly'}
                        onChange={(e) => onIncomeChange(row.id, 'frequency', e.target.value)}
                      >
                        {FREQUENCIES.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        onClick={() => setDeleteTarget({ kind: 'income', row })}
                        aria-label="Delete income source"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Expenses</h2>
          <Button size="sm" variant="outline" onClick={addExpense} disabled={adding === 'expense'} isLoading={adding === 'expense'}>
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
            Add Expense
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium w-36">Amount</th>
                <th className="px-4 py-3 font-medium w-32">Frequency</th>
                <th className="px-4 py-3 font-medium w-12" />
              </tr>
            </thead>
            <tbody>
              {(expenses || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No expenses yet.
                  </td>
                </tr>
              ) : (
                expenseGroups.map((group) => (
                  <React.Fragment key={group.category}>
                    <tr className="bg-gray-50/80">
                      <td colSpan={5} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {group.category}
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.id} className="border-b border-gray-50">
                        <td className="px-4 py-2">
                          <select
                            className={cellInput}
                            tabIndex={-1}
                            value={row.category || 'Other'}
                            onChange={(e) => onExpenseChange(row.id, 'category', e.target.value)}
                          >
                            {EXPENSE_CATEGORIES.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                            {row.category && !EXPENSE_CATEGORIES.includes(row.category) && (
                              <option value={row.category}>{row.category}</option>
                            )}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            className={cellInput}
                            tabIndex={-1}
                            value={row.description || ''}
                            placeholder="Description"
                            onChange={(e) => onExpenseChange(row.id, 'description', e.target.value)}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={`${cellInput} tabular-nums`}
                            tabIndex={amountTabIndexById.get(row.id)}
                            value={row.amount ?? 0}
                            onChange={(e) => onExpenseChange(row.id, 'amount', e.target.value === '' ? 0 : Number(e.target.value))}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <select
                            className={cellInput}
                            tabIndex={-1}
                            value={row.frequency || 'monthly'}
                            onChange={(e) => onExpenseChange(row.id, 'frequency', e.target.value)}
                          >
                            {FREQUENCIES.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                            onClick={() => setDeleteTarget({ kind: 'expense', row })}
                            aria-label="Delete expense"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Cashflow Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Total Annual Income</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums mt-1">{formatCurrency(annualIncome)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Total Annual Expenses</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums mt-1">{formatCurrency(annualExpenses)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Net Income (Annual)</p>
            <p className={`text-lg font-semibold tabular-nums mt-1 ${netIncome < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {formatCurrency(netIncome)}
            </p>
          </div>
        </div>
      </Card>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete row?"
      >
        <p className="text-sm text-gray-500 mb-5">
          Remove this {deleteTarget?.kind === 'income' ? 'income source' : 'expense'}
          {deleteTarget?.row?.description ? (
            <>
              {' '}
              (<span className="font-medium text-gray-800">{deleteTarget.row.description}</span>)
            </>
          ) : null}
          ? This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDelete} isLoading={deleting}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
